import { useCallback, useState } from "react";
import axios from "axios";

import { useMessage } from "./useMessage";
import appconfig from "../config.json";
import { useLoginUser } from "./useLoginUser";
import { DevicePtn, EntityTypePtn } from "../types/api/parameterTypes";

/**
 * パラメータを表すインターフェース
 */
export interface ParameterDefinition {
  /** パラメータストアのパス */
  path: string;
  /** パラメータの表示名 */
  name: string;
}

/**
 * スケジュール設定を表すインターフェース
 */
export interface ScheduleSettings {
  /** パラメータの表示名 */
  name: string;
  /** パラメータのパス */
  parampath: string;
  /** パラメータ定義の配列 */
  parameters: ParameterDefinition[];
  /** Lambda関数名 */
  lambdaName: string;
  /** EventBridgeルール名 */
  eventsRuleName: string;
  /** Step Function ARN */
  stateMachineArn?: string;
  /** 入力変換設定 */
  inputTransform?: any;
}

export const useParameters = () => {
  const { tokenRefresh } = useLoginUser();
  const { showMessage } = useMessage();
  const [devicePtns, setDevicePtns] = useState<Array<DevicePtn>>([]);
  const [deviceNames, setDeviceNames] = useState<Array<string>>([]);
  const [typeNames, setTypeNames] = useState<Array<string>>([]);
  const [typePtns, setTypePtns] = useState<Array<EntityTypePtn>>([]);
  const [tenantJobs, setTenantJobs] = useState<Array<ScheduleSettings>>([]);
  const [parameter, setParameter] = useState<string>();
  const [loading, setLoading] = useState(false);

  const PARMS_MANAGER_URL: string = appconfig["IOT_MANAGEMENT_API_URL"];

  const getDeviceNames = useCallback(() => {
    setLoading(true);
    new Promise((resolve) => resolve(tokenRefresh())).then((token) => {
      axios
        .get<Array<DevicePtn>>(PARMS_MANAGER_URL + "/parameters/device", {
          headers: { Authorization: `Bearer ${token}` },
        })
        .then((res) => {
          const data = res.data.sort((a, b) =>
            a.DeviceName.toLowerCase().localeCompare(b.DeviceName.toLowerCase())
          );
          var names = data.map((val) => val.DeviceName);
          setDevicePtns(data);
          setDeviceNames(names);
          setLoading(false);
        })
        .catch(() => {
          showMessage({
            title: "パラメータの取得に失敗しました",
            status: "error",
          });
          setLoading(false);
        });
    });
  }, []);

  const getTypeNames = useCallback(() => {
    setLoading(true);
    new Promise((resolve) => resolve(tokenRefresh())).then((token) => {
      axios
        .get<Array<EntityTypePtn>>(
          PARMS_MANAGER_URL + "/parameters/entitytype",
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        )
        .then((res) => {
          const data = res.data.sort((a, b) =>
            a.TypeName.toLowerCase().localeCompare(b.TypeName.toLowerCase())
          );
          var names = data.map((val) => {
            return val["TypeName"];
          });
          setTypePtns(data);
          setTypeNames(names);
          setLoading(false);
        })
        .catch(() => {
          showMessage({
            title: "パラメータの取得に失敗しました",
            status: "error",
          });
          setLoading(false);
        });
    });
  }, []);
  const getTenantJobs = useCallback((tenant_id: string) => {
    setLoading(true);
    const path = `${tenant_id}/jobs`;
    new Promise((resolve) => resolve(tokenRefresh())).then((token) => {
      axios
        .get<Array<ScheduleSettings>>(
          PARMS_MANAGER_URL + `/parameters?path=${path}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        )
        .then((res) => {
          const data = res.data;
          setTenantJobs(res.data);
          setLoading(false);
        })
        .catch(() => {
          showMessage({
            title: "パラメータの取得に失敗しました",
            status: "error",
          });
          setLoading(false);
        });
    });
  }, []);

  const getParameter = useCallback((path: string) => {
    setLoading(true);
    if (path) {
      type Param = {
        Value: string;
      };

      new Promise((resolve) => resolve(tokenRefresh())).then((token) => {
        axios
          .get<Param>(PARMS_MANAGER_URL + `/parameter?path=${path}`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          })
          .then((res) => {
            setParameter(res.data.Value);
            setLoading(false);
          })
          .catch(() => {
            showMessage({
              title: "パラメータの取得に失敗しました",
              status: "error",
            });
            setParameter(undefined);
            setLoading(false);
          });
      });
    } else {
      setParameter(undefined);
    }
  }, []);

  const getDevicePtn = useCallback((deviceName: string): DevicePtn | undefined => {
    return devicePtns.find((ptn) => ptn.DeviceName === deviceName);
  }, [devicePtns]);

  return {
    getDeviceNames,
    getTypeNames,
    getTenantJobs,
    getParameter,
    getDevicePtn,
    deviceNames,
    typeNames,
    typePtns,
    tenantJobs,
    parameter,
    loading,
  };
};
